/**
 * ImageAnalyzerService.js
 * Client-side service for analyzing image optimization issues on websites
 */

import htmlFetcherService from './HtmlFetcherService';

class ImageAnalyzerService {
  constructor() {
    this.imageCache = new Map();
    this.maxConcurrent = 5;
    this.activeRequests = 0;
    
    // Get API URL from environment or fallback to default
    const apiPort = process.env.REACT_APP_API_PORT || '5002';
    this.proxyUrl = process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
    
    // Best format for image types
    this.recommendedFormats = {
      'photographic': ['WebP', 'AVIF', 'JPEG'],
      'graphic': ['SVG', 'WebP', 'PNG'],
      'icon': ['SVG', 'WebP'],
      'animation': ['WebP', 'GIF'],
      'transparency': ['WebP', 'PNG']
    };
    
    // Common optimization issues and recommendations
    this.optimizationStrategies = {
      'large-size': [
        'Use a modern image format like WebP or AVIF',
        'Apply appropriate compression without visible quality loss',
        'Remove unnecessary metadata from image files',
        'Consider using a CDN with automatic image optimization'
      ],
      'oversized-dimensions': [
        'Resize images to match their display dimensions',
        'Implement responsive images using srcset and sizes attributes',
        'Serve different image sizes based on device viewport',
        'Avoid scaling down large images with CSS'
      ],
      'lazy-loading': [
        'Add loading="lazy" attribute to images below the fold',
        'Consider using IntersectionObserver for custom lazy loading',
        'Implement progressive loading for large hero images'
      ],
      'accessibility': [
        'Add descriptive alt text to all images',
        'Use empty alt attributes for decorative images',
        'Ensure sufficient color contrast in infographics'
      ]
    };
  }

  /**
   * Analyze images on a URL
   * @param {string} url - The URL to analyze
   * @param {number} sizeThreshold - Size threshold in bytes (default: 500KB)
   * @param {number} dimensionThreshold - Dimension threshold in pixels (default: 1000px)
   * @returns {Promise<Object>} - Analysis results
   */
  async analyzeImages(url, sizeThreshold = 500 * 1024, dimensionThreshold = 1000) {
    try {
      console.log(`[ImageAnalyzerService] Analyzing images for ${url}`);
      
      // Extract images using the proxy to avoid CORS
      const images = await this._extractImages(url);
      
      console.log(`[ImageAnalyzerService] Found ${images.length} images to analyze`);
      
      // Analyze images with the given thresholds
      const imageIssues = await this._analyzeImages(
        images, 
        sizeThreshold, 
        dimensionThreshold
      );
      
      // Generate overall recommendations
      const recommendations = this._generateOverallRecommendations(imageIssues);
      
      // Calculate potential savings
      const totalOriginalSize = imageIssues.reduce((sum, issue) => sum + (issue.size || 0), 0);
      const estimatedSavings = this._calculatePotentialSavings(imageIssues);
      
      // Generate site-wide image optimization score (0-100)
      const score = this._calculateOptimizationScore(imageIssues, images.length);
      
      return {
        url,
        issues: imageIssues,
        totalIssues: imageIssues.length,
        recommendations,
        stats: {
          totalImages: images.length,
          totalImageSize: totalOriginalSize,
          potentialSavings: estimatedSavings,
          optimizationScore: score
        },
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[ImageAnalyzerService] Error analyzing images: ${error.message}`);
      throw error;
    }
  }

  /**
   * Extract images from a URL
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Array>} - Extracted images
   */
  async _extractImages(url) {
    try {
      console.log(`[ImageAnalyzerService] Fetching HTML content for ${url}`);
      
      // Use the shared HTML fetcher service
      const html = await htmlFetcherService.fetchHtml(url);
      
      // Create a parser
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get all image elements
      const imgElements = doc.querySelectorAll('img');
      const images = [];
      
      // Process each image element
      imgElements.forEach(img => {
        // Skip images without src
        const src = img.getAttribute('src');
        if (!src) return;
        
        // Resolve the full URL
        let fullUrl;
        try {
          fullUrl = new URL(src, url).href;
        } catch (urlError) {
          console.warn(`Invalid image URL: ${src}`);
          return;
        }
        
        // Extract image attributes
        const width = parseInt(img.getAttribute('width'), 10) || null;
        const height = parseInt(img.getAttribute('height'), 10) || null;
        const alt = img.getAttribute('alt') || '';
        const loading = img.getAttribute('loading');
        const srcset = img.getAttribute('srcset');
        
        // Check if image has lazy loading or srcset
        const isLazyLoaded = loading === 'lazy' || img.classList.contains('lazy') || img.hasAttribute('data-src');
        
        // Add to the result array
        images.push({
          url: fullUrl,
          src: src,
          alt: alt,
          width: width,
          height: height,
          aspectRatio: width && height ? (width / height).toFixed(2) : null,
          hasWidth: img.hasAttribute('width'),
          hasHeight: img.hasAttribute('height'),
          isLazyLoaded: isLazyLoaded,
          hasSrcset: !!srcset,
          inViewport: this._isElementInInitialViewport(img, width, height)
        });
      });
      
      console.log(`[ImageAnalyzerService] Extracted ${images.length} images from HTML`);
      return images;
    } catch (error) {
      console.error('Error extracting images:', error.message);
      return [];
    }
  }

  /**
   * Analyze a set of images
   * @private
   * @param {Array} images - Array of image objects
   * @param {number} sizeThreshold - Size threshold in bytes
   * @param {number} dimensionThreshold - Dimension threshold in pixels
   * @returns {Promise<Array>} - Array of issues found
   */
  async _analyzeImages(images, sizeThreshold, dimensionThreshold) {
    const issues = [];
    const imagesToAnalyze = [...images];
    
    if (imagesToAnalyze.length === 0) {
      return issues;
    }

    try {
      // Send all image URLs in one batch request
      const response = await fetch(`${this.proxyUrl}/pagespeed/image-infos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          urls: imagesToAnalyze.map(image => image.url)
        })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      const imageInfos = data.images || {};
      
      // Process each image's info
      for (const image of imagesToAnalyze) {
        const imageInfo = imageInfos[image.url] || {};
        const size = imageInfo.size || 0;
        const contentType = imageInfo.type || '';
        
        // Use client-side data for dimensions since server no longer provides them
        let width = image.width || image.naturalWidth || 0;
        let height = image.height || image.naturalHeight || 0;
        
        // If we don't have dimensions yet and we have a DOM element, 
        // try to get dimensions directly from the image
        if ((width === 0 || height === 0) && image.element) {
          try {
            // Create a new image element to load the image and get dimensions
            const imgElement = new Image();
            imgElement.crossOrigin = "Anonymous";
            
            // Get dimensions once the image loads
            const dimensionPromise = new Promise((resolve, reject) => {
              imgElement.onload = () => {
                resolve({
                  width: imgElement.naturalWidth,
                  height: imgElement.naturalHeight
                });
              };
              imgElement.onerror = () => reject(new Error('Failed to load image for dimensions'));
              
              // Set timeout in case image never loads
              setTimeout(() => reject(new Error('Image load timeout')), 5000);
            });
            
            // Set source to trigger loading
            imgElement.src = image.url;
            
            // Wait for image to load and get dimensions
            const dimensions = await dimensionPromise;
            width = dimensions.width;
            height = dimensions.height;
          } catch (dimensionError) {
            console.warn(`Could not determine dimensions for ${image.url}: ${dimensionError.message}`);
            // Continue with whatever dimensions we have (if any)
          }
        }
        
        const format = this._getImageFormat(image.url, contentType);
        
        // Check if displayed dimensions are different from actual dimensions (inappropriate sizing)
        const displayWidth = image.displayWidth || 0;
        const displayHeight = image.displayHeight || 0;
        const isMismatched = displayWidth > 0 && width > 0 && 
                            (width > displayWidth * 2 || height > displayHeight * 2);
        
        // Calculate wasted pixels
        const wastedPixels = isMismatched ? 
          (width * height) - (displayWidth * displayHeight) : 0;
        
        // Calculate potential optimized size
        const estimatedOptimizedSize = this._estimateOptimizedSize(size, format, width, height);
        const potentialSavings = Math.max(0, size - estimatedOptimizedSize);
        
        // Determine image category for format recommendations
        const category = this._determineImageCategory(format, image.url);
        
        // Determine recommended formats
        const recommendedFormats = this.recommendedFormats[category] || this.recommendedFormats.photographic;
        
        // Check issues
        const issueDetails = [];
        
        // Check if image is too large
        if (size > sizeThreshold) {
          issueDetails.push({
            type: 'large-size',
            description: `Large file size (${(size / 1024).toFixed(1)} KB)`,
            impact: 'high',
            recommendations: [
              ...this.optimizationStrategies['large-size'],
              `Convert to ${recommendedFormats[0]} format for better compression`
            ]
          });
        }
        
        // Check if dimensions are too large
        if (width > dimensionThreshold || height > dimensionThreshold) {
          issueDetails.push({
            type: 'oversized-dimensions',
            description: `Large dimensions (${width}×${height} pixels)`,
            impact: 'medium',
            recommendations: this.optimizationStrategies['oversized-dimensions']
          });
        }
        
        // Check for dimension mismatch
        if (isMismatched) {
          issueDetails.push({
            type: 'dimension-mismatch',
            description: `Image display size (${displayWidth}×${displayHeight}) much smaller than actual size (${width}×${height})`,
            impact: 'high',
            recommendations: [
              'Resize image to match display dimensions',
              'Serve appropriately sized images using srcset',
              `Save ~${(potentialSavings / 1024).toFixed(1)} KB by proper sizing`
            ]
          });
        }
        
        // Check if image is missing alt text
        if (image.type === 'img' && !image.alt) {
          issueDetails.push({
            type: 'missing-alt',
            description: 'Missing alt text for accessibility',
            impact: 'low',
            recommendations: this.optimizationStrategies['accessibility']
          });
        }
        
        // Check if image doesn't use lazy loading
        if (image.type === 'img' && image.loading !== 'lazy') {
          issueDetails.push({
            type: 'eager-loading',
            description: 'Not using lazy loading',
            impact: 'medium',
            recommendations: this.optimizationStrategies['lazy-loading']
          });
        }
        
        // Check if image doesn't use modern format
        if (!['image/webp', 'image/avif'].includes(contentType)) {
          issueDetails.push({
            type: 'outdated-format',
            description: `Using ${format || 'unknown'} format instead of modern formats`,
            impact: 'medium',
            recommendations: [
              `Convert to ${recommendedFormats.join(' or ')} for better compression`,
              'Consider using <picture> element with multiple sources',
              'Implement server-side content negotiation for image formats'
            ]
          });
        }
        
        // Check if missing srcset for responsive images
        if (image.type === 'img' && !image.hasSrcset && (width > 600 || height > 600)) {
          issueDetails.push({
            type: 'missing-srcset',
            description: 'Large image missing responsive srcset attribute',
            impact: 'medium',
            recommendations: [
              'Implement responsive images with srcset and sizes attributes',
              'Provide multiple resolution variants for different devices',
              'Use art direction with <picture> element if needed'
            ]
          });
        }
        
        // Create final issue if any were found
        if (issueDetails.length > 0) {
          // Use the highest impact issue as the primary one
          const sortedIssues = [...issueDetails].sort((a, b) => {
            const impactOrder = { high: 0, medium: 1, low: 2 };
            return impactOrder[a.impact] - impactOrder[b.impact];
          });
          
          const primaryIssue = sortedIssues[0];
          
          issues.push({
            type: primaryIssue.type,
            url: image.url,
            description: primaryIssue.description,
            size,
            width,
            height,
            displayWidth,
            displayHeight,
            format,
            potentialSavings,
            estimatedOptimizedSize,
            wastedPixels,
            recommendedFormats,
            issueDetails: sortedIssues,
            impact: primaryIssue.impact,
            recommendations: primaryIssue.recommendations
          });
        }
        
        // Store in cache
        this.imageCache.set(image.url, { 
          size, 
          width, 
          height,
          format,
          issue: issueDetails.length > 0 ? issues[issues.length - 1] : null
        });
      }
      
      return issues;
    } catch (error) {
      console.error('Error analyzing images:', error);
      return [];
    }
  }
  
  /**
   * Get image format from URL and content type
   * @private
   * @param {string} url - Image URL
   * @param {string} contentType - Content-Type header
   * @returns {string} - Image format
   */
  _getImageFormat(url, contentType) {
    // Try to get format from content type
    if (contentType) {
      const match = contentType.match(/image\/(\w+)/i);
      if (match) {
        return match[1].toUpperCase();
      }
    }
    
    // Try to get format from URL extension
    const urlMatch = url.match(/\.(\w+)(?:\?.*)?$/i);
    if (urlMatch) {
      return urlMatch[1].toUpperCase();
    }
    
    return 'Unknown';
  }
  
  /**
   * Determine image category for format recommendations
   * @private
   * @param {string} format - Current image format
   * @param {string} url - Image URL
   * @returns {string} - Image category
   */
  _determineImageCategory(format, url) {
    const urlLower = url.toLowerCase();
    
    // Check if it's likely an icon
    if (urlLower.includes('icon') || urlLower.includes('logo') || format === 'ICO') {
      return 'icon';
    }
    
    // Check if it's likely an animation
    if (format === 'GIF' || urlLower.includes('anim')) {
      return 'animation';
    }
    
    // Check if it's likely a graphic (non-photographic)
    if (format === 'PNG' || format === 'SVG' || urlLower.includes('graphic') || urlLower.includes('chart') || urlLower.includes('diagram')) {
      return 'graphic';
    }
    
    // Default to photographic
    return 'photographic';
  }
  
  /**
   * Estimate optimized size of an image
   * @private
   * @param {number} originalSize - Original size in bytes
   * @param {string} format - Current format
   * @param {number} width - Image width
   * @param {number} height - Image height
   * @returns {number} - Estimated optimized size in bytes
   */
  _estimateOptimizedSize(originalSize, format, width, height) {
    // Default compression ratio estimates
    const compressionRatios = {
      'JPEG': 0.5,    // 50% of original
      'PNG': 0.7,     // 70% of original
      'GIF': 0.6,     // 60% of original
      'WEBP': 0.4,    // 40% of original (already optimized)
      'AVIF': 0.3     // 30% of original (already optimized)
    };
    
    // If original format is already WebP or AVIF, less potential for optimization
    const ratio = compressionRatios[format] || 0.5;
    
    // Calculate based on pixel count and format
    const pixelCount = width * height;
    const bytesPerPixel = originalSize / pixelCount;
    
    // Different compression potential based on size
    let optimizedBytesPerPixel = bytesPerPixel;
    
    if (bytesPerPixel > 1) {
      // High compression potential
      optimizedBytesPerPixel = bytesPerPixel * ratio;
    } else if (bytesPerPixel > 0.5) {
      // Medium compression potential
      optimizedBytesPerPixel = bytesPerPixel * (ratio + 0.1);
    } else {
      // Already well optimized
      optimizedBytesPerPixel = bytesPerPixel * (ratio + 0.2);
    }
    
    return Math.max(1024, Math.round(optimizedBytesPerPixel * pixelCount));
  }
  
  /**
   * Calculate potential savings from all images
   * @private
   * @param {Array} issues - Array of image issues
   * @returns {number} - Total potential savings in bytes
   */
  _calculatePotentialSavings(issues) {
    return issues.reduce((total, issue) => {
      return total + (issue.potentialSavings || 0);
    }, 0);
  }
  
  /**
   * Calculate optimization score (0-100)
   * @private
   * @param {Array} issues - Array of image issues
   * @param {number} totalImages - Total number of images on the page
   * @returns {number} - Score from 0-100
   */
  _calculateOptimizationScore(issues, totalImages) {
    if (totalImages === 0) return 100;
    
    // Count issues by impact
    const highImpactIssues = issues.filter(i => i.impact === 'high').length;
    const mediumImpactIssues = issues.filter(i => i.impact === 'medium').length;
    const lowImpactIssues = issues.filter(i => i.impact === 'low').length;
    
    // Calculate weighted issue score
    const weightedIssueCount = 
      (highImpactIssues * 3) + 
      (mediumImpactIssues * 2) + 
      (lowImpactIssues * 1);
    
    // Maximum possible issues (if all images had all types of issues)
    const maxPossibleIssues = totalImages * 7; // Assuming 7 possible issue types per image
    
    // Calculate score (inverse of weighted issues as percentage of max)
    const rawScore = 100 - (weightedIssueCount / maxPossibleIssues * 100);
    
    // Ensure score is between 0 and 100
    return Math.max(0, Math.min(100, Math.round(rawScore)));
  }
  
  /**
   * Generate overall recommendations based on issues
   * @private
   * @param {Array} issues - Image issues found
   * @returns {Array} - List of overall recommendations
   */
  _generateOverallRecommendations(issues) {
    const recommendations = new Set();
    
    // Count issues by type
    const issueTypes = issues.reduce((counts, issue) => {
      issue.issueDetails.forEach(detail => {
        counts[detail.type] = (counts[detail.type] || 0) + 1;
      });
      return counts;
    }, {});
    
    // Add core recommendations based on issues found
    if (issueTypes['large-size'] > 1) {
      recommendations.add('Implement an image compression pipeline in your build process');
      recommendations.add('Consider using a CDN with automatic image optimization');
    }
    
    if (issueTypes['oversized-dimensions'] > 1) {
      recommendations.add('Establish a responsive image strategy for your site');
      recommendations.add('Use a tool like Sharp or ImageMagick to generate image variants');
    }
    
    if (issueTypes['missing-srcset'] > 1) {
      recommendations.add('Implement responsive images with srcset and sizes attributes');
    }
    
    if (issueTypes['dimension-mismatch'] > 1) {
      recommendations.add('Create a design system with standard image sizes');
      recommendations.add('Resize images server-side rather than with CSS scaling');
    }
    
    if (issueTypes['eager-loading'] > 3) {
      recommendations.add('Implement site-wide lazy loading for below-the-fold images');
    }
    
    if (issueTypes['missing-alt'] > 1) {
      recommendations.add('Add descriptive alt text to all images for accessibility');
    }
    
    if (issueTypes['outdated-format'] > 1) {
      recommendations.add('Serve images in WebP format with JPEG/PNG fallbacks');
      recommendations.add('Use the <picture> element for art direction and format fallbacks');
    }
    
    // Always add these general recommendations
    recommendations.add('Set explicit width and height attributes on images to prevent layout shifts');
    recommendations.add('Optimize critical images that appear above the fold');
    
    return Array.from(recommendations);
  }

  /**
   * Check if an element is likely to be in the initial viewport
   * @private
   * @param {Element} element - The DOM element to check
   * @param {number} width - Element width
   * @param {number} height - Element height
   * @returns {boolean} Whether the element is likely in the initial viewport
   */
  _isElementInInitialViewport(element, width, height) {
    try {
      // Since we can't get actual viewport position from parsed HTML,
      // we'll use a heuristic based on position in the document
      
      // Get all parent elements up to <body>
      let currentElement = element;
      let depth = 0;
      let hasHiddenParent = false;
      
      while (currentElement && currentElement.tagName !== 'BODY' && depth < 10) {
        // Check for elements typically below the fold
        const parentTagName = currentElement.parentElement?.tagName?.toLowerCase();
        
        // Check if inside a footer, sidebar, or other typically below-fold element
        if (['footer', 'aside', 'section'].includes(parentTagName)) {
          const parentId = currentElement.parentElement?.id?.toLowerCase() || '';
          const parentClass = currentElement.parentElement?.className?.toLowerCase() || '';
          
          if (parentId.includes('footer') || 
              parentClass.includes('footer') ||
              parentId.includes('bottom') ||
              parentClass.includes('bottom') ||
              parentId.includes('sidebar') ||
              parentClass.includes('sidebar')) {
            return false;
          }
        }
        
        // Check for hidden elements
        const style = currentElement.getAttribute('style') || '';
        if (style.includes('display: none') || 
            style.includes('visibility: hidden') ||
            style.includes('opacity: 0')) {
          hasHiddenParent = true;
          break;
        }
        
        currentElement = currentElement.parentElement;
        depth++;
      }
      
      if (hasHiddenParent) {
        return false;
      }
      
      // Check for reasonable dimensions
      // Images without dimensions or very small are less likely to be hero images
      if (!width || !height || width < 50 || height < 50) {
        return false;
      }
      
      // Check position in document order - early elements more likely in viewport
      const allElements = document.querySelectorAll('*');
      const elementIndex = Array.from(allElements).indexOf(element);
      
      // First 30% of elements in the document are likely above the fold
      if (elementIndex > 0 && elementIndex < allElements.length * 0.3) {
        return true;
      }
      
      // Default to not in viewport if we can't determine
      return false;
    } catch (error) {
      console.warn('Error determining if element is in viewport:', error);
      return false;
    }
  }
}

// Create a singleton instance
const imageAnalyzerService = new ImageAnalyzerService();
export default imageAnalyzerService; 